import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {GroupBadge} from "../../../../designSystem/components/GroupBadge";
import {getDaysPassed} from "../../model/caseHelpers";
import {timestampToDate} from '../../../../utils/date';
import {STATUS_OPTIONS, STATUS_MAP} from "../../../../utils/constants";
import {DataCardLayout} from "../../../../designSystem/components/DataCardLayout";
import {Typography} from "../../../../designSystem/components/Typography";

export const CaseTable = ({
  cases,
  sortColumn,
  sortOrder,
  selectedStatus,
  userRole,
  organizations,
  handleSort,
  handleOnClickBrowseCase,
  handleOnClickDeleteButton,
}) => (
  <div>
    {/* Desktop Table View */}
    <div className="hidden md:block">
      <table className="w-full table-fixed border shadow-lg rounded-2xl overflow-hidden">
        <thead>
          <tr className="bg-white">
            <th
              className="p-2 border resize-x overflow-auto cursor-pointer w-[200px]"
              onClick={() => handleSort("createdAt")}
            >
              建立時間{" "}
              {sortColumn === "createdAt" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
            </th>
            {selectedStatus === "processing" && (
              <th
                className="p-2 border cursor-pointer resize-x overflow-auto w-[70px]"
                onClick={() => handleSort("daysPassed")}
              >
                已過{" "}
                {sortColumn === "daysPassed" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
              </th>
            )}
            <th
              className="p-2 border cursor-pointer w-[90px]"
              onClick={() => handleSort("category")}
            >
              案件分類{" "}
              {sortColumn === "category" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
            </th>
            <th className="p-2 border resize-x overflow-auto w-[100px]">
              個案姓名
            </th>
            <th
              className="p-2 border cursor-pointer w-[80px]"
              onClick={() => handleSort("patientRegion")}
            >
              住址{" "}
              {sortColumn === "patientRegion" && <FontAwesomeIcon icon={sortOrder === "asc" ? faSortUp : faSortDown}/>}
            </th>
            <th className="p-2 border resize-x overflow-auto w-[80px]">狀態</th>
            {(userRole !== 'worker' && ['accept', 'completed'].includes(selectedStatus)) &&
              <th className="p-2 border resize-x overflow-auto">接案</th>
            }
            {(userRole !== 'worker' && selectedStatus !== 'processing') &&
              <th className="p-2 border resize-x overflow-auto">派案</th>
            }
            <th className="p-2 border resize-x overflow-auto w-[245px]">動作</th>
          </tr>
        </thead>
        <tbody>
        {cases.map((caseItem, index) => (
          <tr key={`case${index}`} className="border bg-white">
            <td className="p-2 border">
              {new Date(timestampToDate(caseItem.createAt)).toLocaleString()}
            </td>
            {selectedStatus === 'processing' &&
              <td className="p-2 border">{getDaysPassed(caseItem.createAt)}天</td>
            }
            <td className="p-2 border">
              <GroupBadge groupName={caseItem.category}/>
            </td>
            <td className="p-2 border">
              {caseItem.patientName || "未命名"}
            </td>
            <td className="p-2 border">
              {caseItem.patientRegion?.replace('屏東縣', '') || ""}
            </td>
            <td className={`p-2 border ${STATUS_MAP[caseItem.status].color} overflow-x-auto whitespace-nowrap`}>
              {STATUS_OPTIONS.find(s => s.value === caseItem.status)?.label || "未知狀態"}
            </td>
            {(userRole !== 'worker' && ['accept', 'completed'].includes(selectedStatus)) &&
              <td className="p-2 border overflow-x-auto whitespace-nowrap">
                {organizations.find(o => o.id === caseItem.acceptedByOrganization)?.name}
              </td>
            }
            {(userRole !== 'worker' && selectedStatus !== 'processing') &&
              <td className="p-2 border overflow-x-auto whitespace-nowrap">
                {organizations.find(o => o.id === caseItem.assignedToOrganization)?.name}
              </td>
            }
            <td className="pl-1 border text-nowrap">
              <ActionButton
                label="瀏覽"
                size="sm"
                onClick={() => handleOnClickBrowseCase(caseItem, 'action')}
                iconName="fa-eye"
                variant="ghost"
              />
              {(['god', 'admin'].includes(userRole) && ['processing', 'assigned', 'rejected'].includes(selectedStatus)) &&
                <ActionButton
                  label="派案"
                  size="sm"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'assign')}
                  iconName="fa-paper-plane"
                  variant="ghost"
                />
              }
              {(['worker'].includes(userRole) && ['assigned', 'processing'].includes(selectedStatus)) &&
                <ActionButton
                  label="接受/拒絕"
                  size="sm"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'accept')}
                  iconName="fa-square-check"
                  variant="ghost"
                />
              }
              {(['god', 'admin', 'worker'].includes(userRole) && ['accept'].includes(selectedStatus)) &&
                <ActionButton
                  label="結案"
                  size="sm"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'report')}
                  iconName="fa-flag"
                  variant="ghost"
                />
              }
              {(['god', 'admin', 'worker'].includes(userRole) && ['completed'].includes(selectedStatus)) &&
                <ActionButton
                  label="滿意度"
                  size="sm"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'survey')}
                  iconName="fa-face-smile"
                  variant="ghost"
                />
              }
              {['god', 'admin'].includes(userRole) &&
                <ActionButton
                  label="刪除"
                  size="sm"
                  onClick={() => handleOnClickDeleteButton(caseItem)}
                  color="warning"
                  iconName="fa-trash"
                  variant="ghost"
                />
              }
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>

    {/* Mobile Card View */}
    <div className="md:hidden space-y-4">
      {cases.map((caseItem, index) => (
        <DataCardLayout
          key={`case${index}`}
          topRowSection={
            <div className="flex flex-1 flex-row justify-between">
              <GroupBadge groupName={caseItem.category} />
              <Typography variant="captionMuted">
                {caseItem.patientRegion ? caseItem.patientRegion.replace('屏東縣', '') : ''}
              </Typography>
            </div>
          }
          mainInfoSection={
            <React.Fragment>
              <Typography variant="title">
                {caseItem.patientName || "未命名"}
              </Typography>

              <div className="flex flex-row gap-2">
                <Typography variant="bodyBold">
                  狀態:
                </Typography>
                <Typography variant="body">
                  {STATUS_OPTIONS.find(s => s.value === caseItem.status)?.label || "未知狀態"}
                </Typography>
              </div>

              <div className="flex flex-row gap-2">
                <Typography variant="bodyBold">
                  建立時間:
                </Typography>
                <Typography variant="body">
                  {new Date(timestampToDate(caseItem.createAt)).toLocaleString()}
                </Typography>
              </div>

              <div className="flex flex-row gap-2">
                <Typography variant="bodyBold">
                  已過:
                </Typography>
                <Typography variant="body">
                  {getDaysPassed(caseItem.createAt)}天
                </Typography>
              </div>

              {(userRole !== 'worker' && ['accept', 'completed'].includes(selectedStatus)) &&
                <div className="flex flex-row gap-2">
                  <Typography variant="bodyBold">
                    接案機構:
                  </Typography>
                  <Typography variant="body">
                    {organizations.find(o => o.id === caseItem.acceptedByOrganization)?.name}
                  </Typography>
                </div>
              }

              {(userRole !== 'worker' && selectedStatus !== 'processing') &&
                <div className="flex flex-row gap-2">
                  <Typography variant="bodyBold">
                    派案機構:
                  </Typography>
                  <Typography variant="body">
                    {organizations.find(o => o.id === caseItem.assignedToOrganization)?.name}
                  </Typography>
                </div>
              }
            </React.Fragment>
          }
          actionSection={
            <React.Fragment>
              <ActionButton
                label="瀏覽"
                onClick={() => handleOnClickBrowseCase(caseItem, '')}
                iconName="fa-eye"
                variant="outline"
              />
              {(['god', 'admin'].includes(userRole) && ['processing', 'assigned', 'rejected'].includes(selectedStatus)) &&
                <ActionButton
                  label="派案"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'assign')}
                  iconName="fa-flag"
                  variant="outline"
                />
              }
              {(['worker'].includes(userRole) && ['assigned', 'processing'].includes(selectedStatus)) &&
                <ActionButton
                  label="接受/拒絕"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'accept')}
                  iconName="fa-square-check"
                  variant="outline"
                />
              }
              {(['god', 'admin', 'worker'].includes(userRole) && ['accept'].includes(selectedStatus)) &&
                <ActionButton
                  label="結案"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'report')}
                  iconName="fa-flag"
                  variant="outline"
                />
              }
              {(['god', 'admin', 'worker'].includes(userRole) && ['completed'].includes(selectedStatus)) &&
                <ActionButton
                  label="滿意度"
                  onClick={() => handleOnClickBrowseCase(caseItem, 'survey')}
                  iconName="fa-face-smile"
                  variant="outline"
                />
              }
              {['god', 'admin'].includes(userRole) &&
                <ActionButton
                  label="刪除"
                  onClick={() => handleOnClickDeleteButton(caseItem)}
                  color="warning"
                  iconName="fa-trash"
                  variant="outline"
                />
              }

            </React.Fragment>
          }
        />
      ))}
    </div>
  </div>
);
